import { ViewColumnInput } from 'types/graphql'

/**
 * Accepts any object with the two fields that `ViewColumnInput` needs.
 * Both `TableViewColumn` (MobX model) and `ViewColumnFragment` (raw
 * GraphQL fragment) satisfy this — keeps one serializer for both the
 * report path and the saved-table-view path.
 */
type ViewColumnInputSource = {
  columnTemplateGeneratedId: string
  displayLabelOverride?: string | null | undefined
}

/**
 * Strips a column source down to the `ViewColumnInput` shape accepted
 * by mutations and queries, dropping `__typename`, `displayLabel`, and
 * any other output-only fields.
 */
export const toViewColumnInput = (c: ViewColumnInputSource): ViewColumnInput => ({
  columnTemplateGeneratedId: c.columnTemplateGeneratedId,
  displayLabelOverride: c.displayLabelOverride,
})

/**
 * Stable string encoding of column identity + display-label overrides.
 * Used as a `useMemo` dep so renames, reorders, adds, and removes all
 * invalidate the memo without deep-comparing object arrays.
 */
export const columnSignature = (columns: readonly ViewColumnInputSource[]): string =>
  columns.map((c) => `${c.columnTemplateGeneratedId}:${c.displayLabelOverride ?? ''}`).join('|')

/**
 * Order-SENSITIVE deep-equal for column-input arrays. Distinct from
 * `areFilterInputArraysEqual` (filters are order-insensitive in the
 * report drawer): admin-configured column order is part of the persisted
 * report — Manage columns lets the user drag to reorder, and the saved
 * order drives both the preview's TanStack columns and the line-item
 * output of the persisted report.
 */
export const areViewColumnInputArraysEqual = (
  a: readonly ViewColumnInput[],
  b: readonly ViewColumnInput[]
): boolean => {
  if (a.length !== b.length) {
    return false
  }
  for (let i = 0; i < a.length; i++) {
    // `i < a.length` and the length-equality check above guarantee both
    // indices are in-bounds, but `noUncheckedIndexedAccess` widens the
    // element type to `T | undefined` regardless. Read into locals so the
    // tsc complaint is silenced without a non-null assertion.
    const aItem = a[i]
    const bItem = b[i]
    if (aItem?.columnTemplateGeneratedId !== bItem?.columnTemplateGeneratedId) {
      return false
    }
    // Compare displayLabelOverride too — popover renames must mark the
    // drawer dirty without the user having to drag-reorder. Treat
    // null-vs-undefined as equal: the BE persists null, but freshly
    // serialized inputs may carry undefined when the field is missing.
    if ((aItem?.displayLabelOverride ?? null) !== (bItem?.displayLabelOverride ?? null)) {
      return false
    }
  }
  return true
}
